/**
 * "Has this scrollable region been read to the bottom?"
 *
 * Used to gate the disclaimer's Accept button. The arithmetic looks trivial and is not, because
 * two ordinary situations both have to answer "yes" and only one of them involves scrolling.
 */

/**
 * Slack allowed when comparing scroll position against height.
 *
 * `scrollTop` is fractional on high-DPI displays and at browser zoom levels other than 100%, so
 * scrollTop + clientHeight routinely lands a pixel or two short of scrollHeight even when the
 * region is scrolled fully to the bottom. Comparing exactly would leave the button disabled for
 * someone who has visibly reached the end and has nowhere further to scroll.
 */
export const SCROLL_END_EPSILON = 4;

const finite = (n) => typeof n === 'number' && Number.isFinite(n);

/**
 * Whether a scrollable element is scrolled to its end.
 *
 * @param {{scrollTop:number, clientHeight:number, scrollHeight:number}} metrics
 * @returns {boolean}
 */
export function hasReachedEnd(metrics) {
  if (!metrics) return true;
  const { scrollTop, clientHeight, scrollHeight } = metrics;

  // Unreadable measurements resolve to "reached", deliberately. This gate exists to encourage
  // reading, not to enforce it — whereas a customer who can never enable the button cannot log in
  // at all. Between a weakened nudge and a hard lockout, the lockout is the worse failure, and it
  // is the one that would arrive as "I can't sign in" rather than as anything visible in testing.
  if (!finite(scrollTop) || !finite(clientHeight) || !finite(scrollHeight)) return true;

  // Content that fits needs no scrolling, and no scroll event will ever fire for it — so if this
  // answered "false" the button would stay disabled forever on a tall window or a short document,
  // a lockout invisible to anyone who tested on a document that scrolls.
  //
  // Stated explicitly even though the comparison below already covers it (scrollTop is 0, so
  // 0 + clientHeight >= scrollHeight - epsilon holds whenever the content fits). Removing this
  // line changes no behaviour today; it is here so the case is named, and so that a future edit
  // to the comparison below cannot quietly reintroduce the lockout.
  if (scrollHeight - clientHeight <= SCROLL_END_EPSILON) return true;

  return scrollTop + clientHeight >= scrollHeight - SCROLL_END_EPSILON;
}

/** Reads the metrics off a DOM node, for callers holding a ref. */
export function hasScrolledToEnd(element) {
  if (!element) return false;   // no element yet: nothing has been read
  return hasReachedEnd({
    scrollTop: element.scrollTop,
    clientHeight: element.clientHeight,
    scrollHeight: element.scrollHeight,
  });
}

export default hasReachedEnd;
